/**
 * ─────────────────────────────────────────────────────────────────────
 * FILE: src/pages/Library.tsx
 * DESCRIPTION: Video and image identity library management interface.
 *              Provides browsing, filtering, and management of verified
 *              media assets with trust scores and watermark verification.
 *
 * FEATURES:
 * - Grid and list view toggle
 * - Advanced filtering (content type, trust score, date, status, watermark)
 * - Search by Persistent ID, filename, or fingerprint
 * - Bulk selection and actions
 * - Quick preview slide-out panel
 * - Sort options (newest, oldest, trust score, risk score)
 * - Thumbnail previews with play buttons for video
 * - Fixed menu flickering issue
 * - Scrollable table view
 * - Proper navigation to identities page
 *
 * @copyright 2025-2026 Anou. All rights reserved.
 * @author Anou
 * @version 2.0.0
 * @created 2026-03-26
 * @updated 2026-03-26 - Removed header code, fixed menu flickering,
 *                       added table scrollbar, fixed navigation
 * ─────────────────────────────────────────────────────────────────────
 */

import { useState, useEffect } from 'react';
import { useUser, useClerk } from '@clerk/react';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  Grid3X3,
  List,
  MoreVertical,
  Download,
  Shield,
  Eye,
  Trash2,
  Copy,
  Check,
  Filter,
  ChevronDown,
  Play,
  X,
  CheckSquare,
  Square,
  Tag,
  FileImage,
  FileVideo,
} from 'lucide-react';

// ─────────────────────────────────────────────────────────────────────
// TYPES
// ─────────────────────────────────────────────────────────────────────
interface Identity {
  id: string;
  filename: string;
  persistentId: string;
  type: 'video' | 'image';
  trustScore: number;
  status: 'original' | 'duplicate';
  watermarkType: 'visible' | 'invisible' | 'none';
  date: string;
  thumbnail: string;
  aspectRatio: '16:9' | '1:1';
  size?: string;
}

interface FilterState {
  contentType: string;
  trustScoreRange: string;
  dateRange: string;
  status: string;
  watermarkType: string;
}

// ─────────────────────────────────────────────────────────────────────
// MOCK DATA
// ─────────────────────────────────────────────────────────────────────
const MOCK_IDENTITIES: Identity[] = [
  {
    id: '1',
    filename: 'VeriMark_Intro_Video_01.mp4',
    persistentId: 'VM7381A',
    type: 'video',
    trustScore: 0.85,
    status: 'original',
    watermarkType: 'visible',
    date: '2026-03-28',
    thumbnail: 'https://images.unsplash.com/photo-1536240478700-b869070f9279?w=400&h=225&fit=crop',
    aspectRatio: '16:9',
    size: '45MB',
  },
  {
    id: '2',
    filename: 'VeriMark_Intro_Video_02.mp4',
    persistentId: 'VM7381B',
    type: 'video',
    trustScore: 0.4,
    status: 'duplicate',
    watermarkType: 'none',
    date: '2026-03-28',
    thumbnail: 'https://images.unsplash.com/photo-1492691527719-9d1e07e534b4?w=400&h=225&fit=crop',
    aspectRatio: '16:9',
    size: '38MB',
  },
  {
    id: '3',
    filename: 'VeriMark_Intro_Video_03.mp4',
    persistentId: 'VM7381C',
    type: 'video',
    trustScore: 0.62,
    status: 'original',
    watermarkType: 'invisible',
    date: '2026-03-28',
    thumbnail: 'https://images.unsplash.com/photo-1574717436401-063d29b06555?w=400&h=225&fit=crop',
    aspectRatio: '16:9',
    size: '52MB',
  },
  {
    id: '4',
    filename: 'Product_Launch_Video.mp4',
    persistentId: 'VM7382A',
    type: 'video',
    trustScore: 0.4,
    status: 'original',
    watermarkType: 'visible',
    date: '2026-03-27',
    thumbnail: 'https://images.unsplash.com/photo-1559131397-f94da358f7ca?w=400&h=225&fit=crop',
    aspectRatio: '16:9',
    size: '125MB',
  },
  {
    id: '5',
    filename: 'Team_Meeting_Recording.mp4',
    persistentId: 'VM7383A',
    type: 'video',
    trustScore: 0.85,
    status: 'original',
    watermarkType: 'invisible',
    date: '2026-03-27',
    thumbnail: 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=400&h=225&fit=crop',
    aspectRatio: '16:9',
    size: '89MB',
  },
  {
    id: '6',
    filename: 'Brand_Logo_Draft.png',
    persistentId: 'VM7384A',
    type: 'image',
    trustScore: 0.62,
    status: 'original',
    watermarkType: 'none',
    date: '2026-03-26',
    thumbnail: 'https://images.unsplash.com/photo-1626785774573-4b7993125651?w=400&h=400&fit=crop',
    aspectRatio: '1:1',
    size: '1.2MB',
  },
  {
    id: '7',
    filename: 'Marketing_Campaign_Video.mp4',
    persistentId: 'VM7385A',
    type: 'video',
    trustScore: 0.42,
    status: 'duplicate',
    watermarkType: 'invisible',
    date: '2026-03-26',
    thumbnail: 'https://images.unsplash.com/photo-1536240478700-b869070f9279?w=400&h=225&fit=crop',
    aspectRatio: '16:9',
    size: '67MB',
  },
];

// ─────────────────────────────────────────────────────────────────────
// UTILITY FUNCTIONS
// ─────────────────────────────────────────────────────────────────────
const getTrustScoreColor = (score: number) => {
  if (score >= 0.7) return 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300';
  if (score >= 0.4) return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300';
  return 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300';
};

const getTrustScoreDotColor = (score: number) => {
  if (score >= 0.7) return 'bg-emerald-600';
  if (score >= 0.4) return 'bg-yellow-600';
  return 'bg-red-600';
};

const getStatusBadge = (status: string) => {
  if (status === 'original') {
    return 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300';
  }
  return 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300';
};

const getWatermarkLabel = (type: string) => {
  switch (type) {
    case 'visible': return '👁️ Visible';
    case 'invisible': return '🔒 Invisible';
    default: return '—';
  }
};

const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

// ─────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────
const Library: React.FC = () => {
  // ───────────────────────────────────────────────────────────────────
  // STATE
  // ───────────────────────────────────────────────────────────────────
  const { user } = useUser();
  const { signOut } = useClerk();
  const navigate = useNavigate();
  
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [previewPanel, setPreviewPanel] = useState<Identity | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [activeMenuId, setActiveMenuId] = useState<string | null>(null);
  
  const [filters, setFilters] = useState<FilterState>({
    contentType: '',
    trustScoreRange: '',
    dateRange: '',
    status: '',
    watermarkType: '',
  });

  const [identities, setIdentities] = useState<Identity[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // ───────────────────────────────────────────────────────────────────
  // EFFECTS
  // ───────────────────────────────────────────────────────────────────
  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      await new Promise(resolve => setTimeout(resolve, 500));
      setIdentities(MOCK_IDENTITIES);
      setIsLoading(false);
    };
    loadData();
  }, []);

  // ───────────────────────────────────────────────────────────────────
  // HANDLERS
  // ───────────────────────────────────────────────────────────────────
  const toggleSelection = (id: string) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
  };

  const selectAll = () => {
    if (selectedIds.size === filteredIdentities.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredIdentities.map(i => i.id)));
    }
  };

  const copyToClipboard = (text: string, id: string) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleFilterChange = (key: keyof FilterState, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const clearFilters = () => {
    setFilters({
      contentType: '',
      trustScoreRange: '',
      dateRange: '',
      status: '',
      watermarkType: '',
    });
    setSearchQuery('');
  };

  // ───────────────────────────────────────────────────────────────────
  // FILTERED & SORTED DATA
  // ───────────────────────────────────────────────────────────────────
  const filteredIdentities = identities.filter(identity => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      if (
        !identity.filename.toLowerCase().includes(query) &&
        !identity.persistentId.toLowerCase().includes(query)
      ) {
        return false;
      }
    }

    if (filters.contentType && identity.type !== filters.contentType) {
      return false;
    }

    if (filters.trustScoreRange) {
      const [min, max] = filters.trustScoreRange.split('-').map(Number);
      if (identity.trustScore < min || identity.trustScore > max) {
        return false;
      }
    }

    if (filters.status && identity.status !== filters.status) {
      return false;
    }

    if (filters.watermarkType && identity.watermarkType !== filters.watermarkType) {
      return false;
    }

    return true;
  }).sort((a, b) => {
    switch (sortBy) {
      case 'newest':
        return new Date(b.date).getTime() - new Date(a.date).getTime();
      case 'oldest':
        return new Date(a.date).getTime() - new Date(b.date).getTime();
      case 'trust-high':
        return b.trustScore - a.trustScore;
      case 'trust-low':
        return a.trustScore - b.trustScore;
      default:
        return 0;
    }
  });

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 12;
  const totalPages = Math.ceil(filteredIdentities.length / itemsPerPage);
  const paginatedIdentities = filteredIdentities.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // ───────────────────────────────────────────────────────────────────
  // RENDER
  // ───────────────────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <div className="p-8">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/4" />
          <div className="grid grid-cols-4 gap-6">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="h-80 bg-gray-200 dark:bg-gray-700 rounded" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      {/* Page Title & Filters */}
      <div className="mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">Video Identities</h1>
          <p className="text-gray-600 dark:text-gray-400">
            Manage and verify your media assets
          </p>
        </div>

        {/* Filter Bar */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700 shadow-sm mt-4">
          <div className="flex items-center gap-4 mb-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by Persistent ID, filename, or fingerprint"
                className="w-full pl-10 pr-4 py-2 border border-gray-200 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600 dark:text-gray-400">Sort by</span>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="px-4 py-2 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
              >
                <option value="newest">Newest</option>
                <option value="oldest">Oldest</option>
                <option value="trust-high">Trust Score (High-Low)</option>
                <option value="trust-low">Trust Score (Low-High)</option>
              </select>
            </div>
          </div>

          {/* Filter Tags */}
          <div className="flex items-center gap-3 flex-wrap">
            <select
              value={filters.contentType}
              onChange={(e) => handleFilterChange('contentType', e.target.value)}
              className="flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-gray-700 rounded-lg text-sm text-gray-700 dark:text-gray-300 border-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Content Type</option>
              <option value="video">Video</option>
              <option value="image">Image</option>
            </select>
            
            <select
              value={filters.trustScoreRange}
              onChange={(e) => handleFilterChange('trustScoreRange', e.target.value)}
              className="flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-gray-700 rounded-lg text-sm text-gray-700 dark:text-gray-300 border-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Trust Score Range</option>
              <option value="0.7-1">High (0.7-1.0)</option>
              <option value="0.4-0.7">Medium (0.4-0.7)</option>
              <option value="0-0.4">Low (0-0.4)</option>
            </select>
            
            <select
              value={filters.dateRange}
              onChange={(e) => handleFilterChange('dateRange', e.target.value)}
              className="flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-gray-700 rounded-lg text-sm text-gray-700 dark:text-gray-300 border-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Date Range</option>
              <option value="today">Today</option>
              <option value="week">This Week</option>
              <option value="month">This Month</option>
              <option value="year">This Year</option>
            </select>
            
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-gray-700 rounded-lg text-sm text-gray-700 dark:text-gray-300 border-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Status</option>
              <option value="original">Original</option>
              <option value="duplicate">Duplicate</option>
            </select>
            
            <select
              value={filters.watermarkType}
              onChange={(e) => handleFilterChange('watermarkType', e.target.value)}
              className="flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-gray-700 rounded-lg text-sm text-gray-700 dark:text-gray-300 border-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Watermark Type</option>
              <option value="visible">Visible</option>
              <option value="invisible">Invisible</option>
              <option value="none">None</option>
            </select>

            <div className="flex-1" />
            
            {(searchQuery || Object.values(filters).some(f => f)) && (
              <button
                onClick={clearFilters}
                className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
              >
                Clear All
              </button>
            )}
            
            {/* View Toggle */}
            <div className="flex items-center gap-1 bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded-md transition-colors ${
                  viewMode === 'grid'
                    ? 'bg-white dark:bg-gray-600 shadow-sm'
                    : 'text-gray-600 dark:text-gray-400'
                }`}
              >
                <Grid3X3 className="w-5 h-5" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded-md transition-colors ${
                  viewMode === 'list'
                    ? 'bg-white dark:bg-gray-600 shadow-sm'
                    : 'text-gray-600 dark:text-gray-400'
                }`}
              >
                <List className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Bulk Actions Bar */}
        {selectedIds.size > 0 && (
          <div className="mt-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-4 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <span className="text-sm font-medium text-blue-900 dark:text-blue-300">
                {selectedIds.size} selected
              </span>
              <button
                onClick={selectAll}
                className="text-sm text-blue-700 dark:text-blue-400 hover:underline"
              >
                Select All
              </button>
            </div>
            <div className="flex items-center gap-3">
              <button className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-800 border border-blue-300 dark:border-blue-700 rounded-lg text-sm font-medium text-blue-700 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30">
                <Download className="w-4 h-4" />
                Export Selected
              </button>
              <button className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-800 border border-blue-300 dark:border-blue-700 rounded-lg text-sm font-medium text-blue-700 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30">
                <Shield className="w-4 h-4" />
                Verify Watermarks
              </button>
              <button className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-800 border border-blue-300 dark:border-blue-700 rounded-lg text-sm font-medium text-blue-700 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30">
                <Tag className="w-4 h-4" />
                Add Tags
              </button>
              <button className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 rounded-lg text-sm font-medium text-white">
                <Trash2 className="w-4 h-4" />
                Delete
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Identity Grid */}
      <div className="mb-6">
        <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4">
          IDENTITY GRID ({filteredIdentities.length})
        </h2>
        
        {viewMode === 'grid' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {paginatedIdentities.map((identity) => (
              <div
                key={identity.id}
                onClick={() => navigate(`/identities/${identity.id}`)}
                className="group bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden hover:shadow-lg hover:-translate-y-1 transition-all duration-200 cursor-pointer"
              >
                {/* Thumbnail */}
                <div className="relative aspect-video bg-gray-100 dark:bg-gray-700">
                  <img
                    src={identity.thumbnail}
                    alt={identity.filename}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.currentTarget.src = identity.type === 'video'
                        ? 'https://via.placeholder.com/400x225/1a365d/ffffff?text=Video'
                        : 'https://via.placeholder.com/400x400/1a365d/ffffff?text=Image';
                    }}
                  />
                  {identity.type === 'video' && (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-12 h-12 bg-white/90 dark:bg-gray-800/90 rounded-full flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                        <Play className="w-5 h-5 text-gray-900 dark:text-white ml-1" />
                      </div>
                    </div>
                  )}
                  <div className="absolute top-2 left-2 px-2 py-1 bg-black/70 text-white text-xs rounded">
                    {identity.aspectRatio}
                  </div>
                  
                  {/* 3-Dot Menu - FIXED FLICKERING */}
                  <div
                    className="absolute top-2 right-2"
                    onMouseEnter={() => setActiveMenuId(identity.id)}
                    onMouseLeave={() => setActiveMenuId(null)}
                  >
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setActiveMenuId(activeMenuId === identity.id ? null : identity.id);
                      }}
                      className="p-2 bg-white/90 dark:bg-gray-800/90 rounded-lg hover:bg-white dark:hover:bg-gray-700 transition-colors"
                    >
                      <MoreVertical className="w-4 h-4 text-gray-900 dark:text-white" />
                    </button>
                    
                    {activeMenuId === identity.id && (
                      <div
                        onMouseEnter={() => setActiveMenuId(identity.id)}
                        onMouseLeave={() => setActiveMenuId(null)}
                        className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-50"
                      >
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/identities/${identity.id}`);
                          }}
                          className="w-full flex items-center gap-3 px-4 py-3 text-sm text-gray-700 dark:text-gray-300 hover:bg-slate-800 hover:text-white rounded-t-lg transition-colors"
                        >
                          <Eye className="w-4 h-4" />
                          View Full Analysis
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            // Handle download
                            setActiveMenuId(null);
                          }}
                          className="w-full flex items-center gap-3 px-4 py-3 text-sm text-gray-700 dark:text-gray-300 hover:bg-slate-800 hover:text-white transition-colors"
                        >
                          <Download className="w-4 h-4" />
                          Download
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            // Handle verify
                            setActiveMenuId(null);
                          }}
                          className="w-full flex items-center gap-3 px-4 py-3 text-sm text-gray-700 dark:text-gray-300 hover:bg-slate-800 hover:text-white transition-colors"
                        >
                          <Shield className="w-4 h-4" />
                          Verify Watermark
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            // Handle delete
                            setActiveMenuId(null);
                          }}
                          className="w-full flex items-center gap-3 px-4 py-3 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-b-lg transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                          Delete
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {/* Card Content */}
                <div className="p-4">
                  <h3 className="font-medium text-gray-900 dark:text-white truncate mb-1">
                    {identity.filename}
                  </h3>
                  
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-sm text-gray-600 dark:text-gray-400 font-mono">
                      {identity.persistentId}
                    </span>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        copyToClipboard(identity.persistentId, identity.id);
                      }}
                      className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                    >
                      {copiedId === identity.id ? (
                        <Check className="w-3 h-3 text-green-600" />
                      ) : (
                        <Copy className="w-3 h-3 text-gray-400" />
                      )}
                    </button>
                  </div>

                  <div className="flex items-center gap-2 mb-3">
                    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${getTrustScoreColor(identity.trustScore)}`}>
                      <div className={`w-2 h-2 rounded-full ${getTrustScoreDotColor(identity.trustScore)}`} />
                      {identity.trustScore.toFixed(2)}/1.0
                    </span>
                    
                    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${getStatusBadge(identity.status)}`}>
                      {identity.status === 'original' && <Check className="w-3 h-3" />}
                      {identity.status.charAt(0).toUpperCase() + identity.status.slice(1)}
                    </span>
                  </div>

                  <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                    <span>{getWatermarkLabel(identity.watermarkType)}</span>
                    <span>{formatDate(identity.date)}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          /* List View - WITH SCROLLBAR */
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="max-h-[600px] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600 scrollbar-track-transparent hover:scrollbar-thumb-gray-400 dark:hover:scrollbar-thumb-gray-500">
              <table className="w-full">
                <thead className="bg-gray-50 dark:bg-gray-700 sticky top-0 z-10">
                  <tr>
                    <th className="w-12 px-4 py-3">
                      <button onClick={selectAll} className="hover:opacity-70">
                        {selectedIds.size === paginatedIdentities.length ? (
                          <CheckSquare className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                        ) : (
                          <Square className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                        )}
                      </button>
                    </th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-gray-700 dark:text-gray-300">Thumbnail</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-gray-700 dark:text-gray-300">Filename</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-gray-700 dark:text-gray-300">Persistent ID</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-gray-700 dark:text-gray-300">Type</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-gray-700 dark:text-gray-300">Trust Score</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-gray-700 dark:text-gray-300">Watermark</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-gray-700 dark:text-gray-300">Created</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-gray-700 dark:text-gray-300">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {paginatedIdentities.map((identity) => (
                    <tr key={identity.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                      <td className="px-4 py-3">
                        <button onClick={() => toggleSelection(identity.id)} className="hover:opacity-70">
                          {selectedIds.has(identity.id) ? (
                            <CheckSquare className="w-5 h-5 text-blue-600" />
                          ) : (
                            <Square className="w-5 h-5 text-gray-400" />
                          )}
                        </button>
                      </td>
                      <td className="px-4 py-3">
                        <div className="w-16 h-12 bg-gray-100 dark:bg-gray-700 rounded overflow-hidden">
                          <img src={identity.thumbnail} alt="" className="w-full h-full object-cover" />
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">{identity.filename}</td>
                      <td className="px-4 py-3 text-sm font-mono text-gray-600 dark:text-gray-400">{identity.persistentId}</td>
                      <td className="px-4 py-3">
                        {identity.type === 'video' ? (
                          <FileVideo className="w-4 h-4 text-purple-600" />
                        ) : (
                          <FileImage className="w-4 h-4 text-blue-600" />
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${getTrustScoreColor(identity.trustScore)}`}>
                          {identity.trustScore.toFixed(2)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">{getWatermarkLabel(identity.watermarkType)}</td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">{formatDate(identity.date)}</td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => navigate(`/identities/${identity.id}`)}
                          className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                        >
                          <MoreVertical className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {/* Pagination - Outside scroll area */}
            <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <span className="text-sm text-gray-600 dark:text-gray-400">
                Showing {(currentPage - 1) * itemsPerPage + 1}-{Math.min(currentPage * itemsPerPage, filteredIdentities.length)} of {filteredIdentities.length}
              </span>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded-lg text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
                >
                  Previous
                </button>
                {[...Array(totalPages)].map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setCurrentPage(i + 1)}
                    className={`px-3 py-1 rounded-lg text-sm ${
                      currentPage === i + 1
                        ? 'bg-blue-600 text-white'
                        : 'border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
                    }`}
                  >
                    {i + 1}
                  </button>
                ))}
                <button
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded-lg text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Quick Preview Panel */}
      {previewPanel && (
        <div className="fixed inset-0 z-50 flex justify-end">
          <div className="absolute inset-0 bg-black/50" onClick={() => setPreviewPanel(null)} />
          <div className="relative w-96 bg-white dark:bg-gray-800 h-full shadow-2xl overflow-y-auto">
            <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 p-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Identity Details</h3>
              <button
                onClick={() => setPreviewPanel(null)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
              >
                <X className="w-5 h-5 text-gray-600 dark:text-gray-400" />
              </button>
            </div>

            <div className="p-6">
              {/* Thumbnail */}
              <div className="aspect-video bg-gray-100 dark:bg-gray-700 rounded-lg mb-4 overflow-hidden">
                <img src={previewPanel.thumbnail} alt="" className="w-full h-full object-cover" />
              </div>

              <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                {previewPanel.filename}
              </h4>
              
              <div className="space-y-4">
                <div>
                  <label className="text-sm text-gray-600 dark:text-gray-400">Persistent ID</label>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="font-mono text-gray-900 dark:text-white">{previewPanel.persistentId}</span>
                    <button
                      onClick={() => copyToClipboard(previewPanel.persistentId, 'preview')}
                      className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                    >
                      {copiedId === 'preview' ? (
                        <Check className="w-4 h-4 text-green-600" />
                      ) : (
                        <Copy className="w-4 h-4 text-gray-400" />
                      )}
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-gray-600 dark:text-gray-400">Trust Score</label>
                    <p className="font-medium text-gray-900 dark:text-white mt-1">{previewPanel.trustScore.toFixed(2)}</p>
                  </div>
                  <div>
                    <label className="text-sm text-gray-600 dark:text-gray-400">Status</label>
                    <p className="font-medium text-gray-900 dark:text-white mt-1 capitalize">{previewPanel.status}</p>
                  </div>
                </div>

                <div>
                  <label className="text-sm text-gray-600 dark:text-gray-400">Watermark</label>
                  <p className="font-medium text-gray-900 dark:text-white mt-1">{getWatermarkLabel(previewPanel.watermarkType)}</p>
                </div>

                <div>
                  <label className="text-sm text-gray-600 dark:text-gray-400">Created</label>
                  <p className="font-medium text-gray-900 dark:text-white mt-1">{formatDate(previewPanel.date)}</p>
                </div>
              </div>

              {/* Actions */}
              <div className="mt-6 space-y-3">
                <button className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-slate-800 hover:bg-slate-900 dark:bg-slate-700 dark:hover:bg-slate-600 text-white rounded-lg font-medium">
                  <Download className="w-5 h-5" />
                  Download
                </button>
                <button className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 text-gray-900 dark:text-white rounded-lg font-medium">
                  <Shield className="w-5 h-5" />
                  Verify Watermark
                </button>
                <button className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 text-gray-900 dark:text-white rounded-lg font-medium">
                  <Eye className="w-5 h-5" />
                  View Full Analysis
                </button>
                <button className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/30 text-red-700 dark:text-red-400 rounded-lg font-medium">
                  <Trash2 className="w-5 h-5" />
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Click outside to close menus */}
      {activeMenuId && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setActiveMenuId(null)}
        />
      )}
    </div>
  );
};

export default Library;
